import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Scope-aware sidebar hiding, announced by a module.
 *
 * A microfrontend can tell the shell that some of its sidebar rows are
 * meaningless for the scope the user has selected inside it — CBAM, for
 * instance, hides the importer-only groups while an operator (exporter)
 * profile is selected (apps/cbam-mfe/src/components/NavScopeBroadcaster.tsx).
 *
 * The announcement is a window event plus a sessionStorage copy, so the
 * sidebar is right on the first paint after a reload. Each announcement may be
 * conditional on a URL query parameter (`when`): the rows stay hidden while
 * the current URL carries that value or no value at all (sidebar links are
 * plain paths, and the module restores its remembered scope on arrival), and
 * are shown again the moment the URL names a different value — so a stale
 * announcement never hides navigation for a scope the user has left.
 *
 * Only the rendered sidebar and the command palette consult this. Route
 * resolution, plan entitlements and role permissions keep the full menu tree.
 */
export const MODULE_NAV_SCOPE_EVENT = 'esgcaffe:module-nav-scope';
export const MODULE_NAV_SCOPE_STORAGE_PREFIX = 'esgcaffe:module-nav-scope:';
export interface ModuleNavScope {
  module: string;
  hiddenPaths: string[];
  when?: { searchParam: string; equals: string };
}

const normalise = (path: string) => (path || '').toLowerCase().replace(/\/+$/, '');

function isModuleNavScope(x: unknown): x is ModuleNavScope {
  if (!x || typeof x !== 'object') return false;
  const s = x as Partial<ModuleNavScope>;
  if (typeof s.module !== 'string' || !s.module) return false;
  if (!Array.isArray(s.hiddenPaths) || !s.hiddenPaths.every((p) => typeof p === 'string')) return false;
  if (s.when === undefined) return true;
  return !!s.when && typeof s.when.searchParam === 'string' && typeof s.when.equals === 'string';
}

function readStored(): Record<string, ModuleNavScope> {
  const out: Record<string, ModuleNavScope> = {};
  try {
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (!key || !key.startsWith(MODULE_NAV_SCOPE_STORAGE_PREFIX)) continue;
      const raw = sessionStorage.getItem(key);
      if (!raw) continue;
      const parsed: unknown = JSON.parse(raw);
      if (isModuleNavScope(parsed)) out[parsed.module] = parsed;
    }
  } catch {
    // Storage unavailable or a malformed entry: start unfiltered; the live event still arrives.
  }
  return out;
}

/** Returns a predicate: is this menu path hidden by a module's current scope announcement? */
export function useModuleNavScope(): (path: string) => boolean {
  const { search } = useLocation();
  const [scopes, setScopes] = useState<Record<string, ModuleNavScope>>(readStored);

  useEffect(() => {
    const onScope = (e: Event) => {
      const detail = (e as CustomEvent<unknown>).detail;
      if (isModuleNavScope(detail)) setScopes((prev) => ({ ...prev, [detail.module]: detail }));
    };
    window.addEventListener(MODULE_NAV_SCOPE_EVENT, onScope);
    return () => window.removeEventListener(MODULE_NAV_SCOPE_EVENT, onScope);
  }, []);

  const hidden = useMemo(() => {
    const params = new URLSearchParams(search);
    const set = new Set<string>();
    for (const scope of Object.values(scopes)) {
      if (scope.when) {
        const current = params.get(scope.when.searchParam);
        if (current !== null && current !== scope.when.equals) continue;
      }
      for (const p of scope.hiddenPaths) set.add(normalise(p));
    }
    return set;
  }, [scopes, search]);

  return useCallback((path: string) => hidden.size > 0 && hidden.has(normalise(path)), [hidden]);
}
